<template>
  <form
    class="add-new-spousal-form clearfixing"
    name="addNewForm"
    novalidate
    @submit.prevent="onAddNewFormSubmitted"
    autocomplete="off"
  >
    <input autocomplete="off" name="hidden" type="text" hidden>

    <!-- INPUT FIELDS -->
    <section class="section-col">

      <!-- PAYOR ( ALIMONY, CHILD ) -->
      <div class="part" v-if="type !== $SHARED_EXPENSE_TYPE">
        <p class="label input-filter-label">{{ $lang.messages.payor }}</p>
        <div class="wrapper">
          <autocomplete
            v-if="actionType === 'add' || (actionType === 'edit' && payment.payor.guid !== '')"
            :arrEditRecurring="arrForSchedulePayment"
            :items="coparentsPayor"
            :placeholder="coparentName"
            :option="payment.payor"
            :name="'payor'"
            :bluredProp="bluredProp"
            :paymentsProp="true"
            @optionChanged="onSelectedPayor"
            @clearInputErrors="clearInputErrors"
            @clearOthers="clearOthers"
            :key="rerender"
            ref="autocompletePayor" />
        </div>
      </div>

      <!-- RECIPIENT ( ALIMONY, CHILD ) -->
      <div class="part" v-if="type !== $SHARED_EXPENSE_TYPE">
        <p class="label input-filter-label">{{ $lang.messages.recipient }}</p>
        <div class="wrapper">
          <autocomplete
            v-if="actionType === 'add' || (actionType === 'edit' && payment.recipient.guid !== '')"
            :arrEditRecurring="arrForSchedulePayment"
            :items="coparentsRecipient"
            :placeholder="$lang.messages.selectCoparents"
            :option="payment.recipient"
            :name="'recipient'"
            :bluredProp="bluredProp"
            :paymentsProp="true"
            @optionChanged="onSelectedRecipient"
            @clearInputErrors="clearInputErrors"
            @clearOthers="clearOthers"
            :key="rerender"
            ref="autocompleteRecipient" />
        </div>
      </div>

      <!-- ADD CHILDREN ( CHILD ) -->
      <div class="part" v-if="type === $CHILD_EXPENSE_TYPE">
        <p class="label input-filter-label">{{ $lang.messages.children }}</p>
        <div class="wrapper">
          <children-checklist
            :items="children"
            :placeholder="$lang.messages.selectChildren"
            :option="payment.chosenChildren"
            :name="'children'"
            :bluredProp="bluredProp"
            :additionalValidationRule="payment.payor.name !== '' && payment.recipient.name !== ''"
            @optionChanged="onSelectedChildren"
            @clearInputErrors="clearInputErrors"
            @clearOthers="clearOthers"
            :key="rerenderChecklist"
            ref="childrenChecklist" />
        </div>
      </div>

      <!-- DESCRIPTION ( SHARED ) -->
      <div class="part" v-if="type === $SHARED_EXPENSE_TYPE">
        <p class="label input-filter-label">{{ $lang.messages.description }}</p>
        <div class="input-wrapper input-field long-input-wrapper" :class="{'error-input' : errors.has('description')}">
          <input
            type="text"
            name="description"
            class="input"
            :placeholder="$lang.messages.enterDescription"
            v-model="payment.description"
            v-validate="'required|min:1|max:255'"
            @focus.self="clearInputErrors"
            @blur="clearOthers"
          />
          <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon">
          <div class="error-tooltip" v-show="errors.has('description')" :class="{'hide-tooltip' : bluredProp !== 'description'}">
            <div class="text">
              {{ errors.first('description') }}
            </div>
          </div>
        </div>
      </div>

      <!-- ADD CHILDREN ( SHARED ) -->
      <div class="part" v-if="type === $SHARED_EXPENSE_TYPE">
        <p class="label input-filter-label">{{ $lang.messages.children }}</p>
        <div class="wrapper">
          <cases-checklist
            :items="cases"
            :option="payment.casesExists"
            :placeholder="$lang.messages.selectChildren"
            :name="'children'"
            :bluredProp="bluredProp"
            @optionChanged="onSelectedCases"
            @clearInputErrors="clearInputErrors"
            @clearOthers="clearOthers"
            :key="rerenderChecklist"
            ref="childrenCasesChecklist" />
        </div>
      </div>

      <!-- PAYMENT DATE -->
      <div class="part datepicker-part">
        <p class="label input-filter-label">
          {{
            !QUERY_PATH ? $lang.messages.paymentDate : QUERY_PATH && actionType === 'add' ?
            $lang.messages.dateOfFirstPayment : $lang.messages.editDateOfFirstPayment
          }}
        </p>
        <f-p-datepicker
          :option="payment.date.toString()"
          :forbidPast="true"
          @optionChanged="onSelectedPaymentDate">
        </f-p-datepicker>
      </div>

      <!-- FREQUENCY, INTERVAL DURATION, ENDS ON -->
      <template v-if="QUERY_PATH">
        <!-- FREQUENCY -->
        <div class="part middle-part">
          <p class="label input-filter-label">{{ $lang.messages.repeatEvery }}</p>
          <div class="wrapper extra-short-input-wrapper">
            <autocomplete
              v-if="payment.frequencyOption.value !== ''"
              :items="frequencyOptions"
              :placeholder="$lang.messages.weekly"
              :option="payment.frequencyOption"
              :name="'interval'"
              :bluredProp="bluredProp"
              :middleSize="true"
              @optionChanged="onSelectedFrequencyOption"
              @clearInputErrors="clearInputErrors"
              @clearOthers="clearOthers"
              ref="autocompleteIntervalUnit"/>
          </div>
        </div>

        <!-- INTERVAL DURATION -->
        <div class="part">
          <div v-if="payment.frequencyOption.value === 'custom'">
            <p class="label input-filter-label">{{ $lang.messages.every }}</p>
            <div class="input-wrapper input-field extra-short-input-wrapper" :class="{'error-input' : errors.has('intervalDuration')}">
              <input
                type="text"
                name="intervalDuration"
                class="input"
                v-model="payment.intervalDuration"
                v-validate="{
                  min_value: 1,
                  max_value: 100,
                  numeric: true
                }"
                @change="drawCalendar"
                @focus.self="clearInputErrors"
                @blur="clearOthers"
                @keypress="MX_preventCharInput"
                @paste.prevent
                :min="1"
                :max="100"
              />
              <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon error-icon">
              <div class="error-tooltip" v-show="errors.has('intervalDuration')" :class="{'hide-tooltip' : bluredProp !== 'intervalDuration'}">
                <div class="text">
                  {{ errors.first('intervalDuration') }}
                </div>
              </div>
            </div>
            <div class="wrapper short-input-wrapper">
              <autocomplete
                v-if="payment.intervalUnit.value !== ''"
                :items="intervalUnits"
                :placeholder="$lang.messages.week"
                :option="payment.intervalUnit"
                :name="'interval'"
                :bluredProp="bluredProp"
                :middleSize="true"
                @optionChanged="onSelectedIntervalUnit"
                @clearInputErrors="clearInputErrors"
                @clearOthers="clearOthers"
                ref="autocompleteIntervalUnit"/>
            </div>
          </div>
        </div>

        <!-- ENDS ON -->
        <div class="part middle-part">
          <p class="label input-filter-label">{{ $lang.messages.end }}</p>
          <div class="wrapper extra-short-input-wrapper">
            <autocomplete
              v-if="payment.endUnit.value !== ''"
              :items="endUnits"
              :placeholder="$lang.messages.on"
              :option="payment.endUnit"
              :name="'endUnit'"
              :bluredProp="bluredProp"
              :smallSize="true"
              @optionChanged="onSelectedEndUnit"
              @clearInputErrors="clearInputErrors"
              @clearOthers="clearOthers"
              ref="autocompleteEndUnit"/>
          </div>
          <div class="part datepicker-part datepicker-part-long" v-if="payment.endUnit.value === 'on'">
            <f-p-datepicker
              :option="payment.endDate.toString()"
              :forbidFromComparedDate="true"
              :comparedDate="new Date(payment.date)"
              @optionChanged="onSelectedEndDate">
            </f-p-datepicker>
          </div>

          <!-- ENDS AFTER -->
          <div
            class="input-wrapper input-field special-short-input-wrapper"
            v-if="payment.endUnit.value === 'after'"
            :class="{'error-input' : errors.has('endDuration')}"
          >
            <input
              type="text"
              name="endDuration"
              class="input"
              v-model="payment.endDuration"
              v-validate="{
                min_value: 1,
                max_value: 100,
                numeric: true
              }"
              @change="drawCalendar"
              @focus.self="clearInputErrors"
              @blur="clearOthers"
              @keypress="MX_preventCharInput"
              @paste.prevent
              :min="1"
              :max="100"
            />
            <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon error-icon">
            <div class="error-tooltip" v-show="errors.has('endDuration')" :class="{'hide-tooltip' : bluredProp !== 'endDuration'}">
              <div class="text">
                {{ errors.first('endDuration') }}
              </div>
            </div>
          </div>
          <div class="text-wrapper" v-if="payment.endUnit.value === 'after'">
            {{ $lang.messages.occurrences }}
          </div>
        </div>
      </template>

      <!-- AMOUNT ( ALIMONY, CHILD ) -->
      <div class="part middle-part" v-if="type !== $SHARED_EXPENSE_TYPE">
        <p class="label input-filter-label">{{ $lang.messages.amount }}</p>
        <div class="input-wrapper input-field" :class="{'error-input' : errors.has('amount')}">
          <input
            type="number"
            name="amount"
            class="input input-currency"
            placeholder="1"
            v-model="payment.amount"
            v-validate="'required|min_value:1|decimal:2'"
            @focus.self="clearInputErrors"
            @blur="clearOthers"
            @keypress="MX_preventCharInput"
            @paste.prevent
            min="1"
            :max="MX_maxInputValue"
          />
          <span class="input-currency-sign">
            <currency-sign :currency="currency" />
          </span>
          <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon">
          <div class="error-tooltip" v-show="errors.has('amount')" :class="{'hide-tooltip' : bluredProp !== 'amount'}">
            <div class="text">
              {{ errors.first('amount') }}
            </div>
          </div>
        </div>
      </div>

      <!-- SHARE RATIO -->
      <div class="part section-col-separator" v-if="type === $SHARED_EXPENSE_TYPE">
        {{ $lang.messages.shareRatio }}
      </div>

      <!-- TOTAL AMOUNT ( SHARED ) -->
      <div class="part short-part pay-m-right-50 width-40" v-if="type === $SHARED_EXPENSE_TYPE">
        <p class="label input-filter-label">{{ $lang.messages.totalAmount }}</p>
        <div class="input-wrapper input-field small-input-wrapper" :class="{'error-input' : errors.has('totalAmount')}">
          <input
            type="number"
            name="totalAmount"
            class="input input-currency"
            placeholder="1"
            v-model="payment.totalAmount"
            v-validate="'required|min_value:1|decimal:2'"
            onkeydown="return event.keyCode !== 69"
            @input="lastUpdated = 'totalAmount'"
            @click="checkValidationForSharedAmount"
            @focus.self="clearInputErrors"
            @blur="clearOthers"
            @keypress="MX_preventCharInput"
            @paste.prevent
            min="1"
            :max="MX_maxInputValue"
          />
          <span class="input-currency-sign">
            <currency-sign :currency="currency" />
          </span>
          <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon">
          <div class="error-tooltip" v-show="errors.has('totalAmount')" :class="{'hide-tooltip' : bluredProp !== 'totalAmount'}">
            <div class="text">
              {{ errors.first('totalAmount') }}
            </div>
          </div>
        </div>
      </div>

      <!-- PAID BY ( SHARED ) -->
      <div class="part short-part width-40" v-if="type === $SHARED_EXPENSE_TYPE">
        <p class="label input-filter-label">{{ $lang.messages.origPaidBy }}</p>
        <div class="wrapper">
          <autocomplete
            v-if="actionType === 'add' || (actionType === 'edit' && payment.paidByBoolean)"
            :items="coparents"
            :placeholder="$lang.messages.totalAmountPaidBy"
            :option="payment.paidBy"
            :name="'paidBy'"
            :bluredProp="bluredProp"
            :middleSize="true"
            :paymentsProp="true"
            @optionChanged="onSelectedPaidBy"
            @clearInputErrors="clearInputErrors"
            @clearOthers="clearOthers"
            :key="rerender"
            ref="autocompletePaidBy" />
        </div>
      </div>

      <!-- MY SHARE ( SHARED ) -->
      <div class="part extra-short-left-part pay-m-right-50 width-40" v-if="type === $SHARED_EXPENSE_TYPE">
        <p class="label input-filter-label">{{ $lang.messages.myShare }}</p>
        <div class="input-wrapper input-field" :class="{'error-input' : errors.has('myShare')}">
          <input
            type="number"
            name="myShare"
            class="input input-currency"
            placeholder="0"
            v-model="payment.myShare"
            required
            v-validate
            onkeypress="return event.charCode >= 48 && event.charCode <= 57"
            onkeydown="return event.keyCode !== 69"
            @input="lastUpdated = 'myShare'"
            @focus.self="clearInputErrors"
            @blur="clearOthers"
            min="0"
            max="100"
          />
          <span class="input-currency-sign">%</span>
          <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon">
          <div class="error-tooltip" v-show="errors.has('myShare')" :class="{'hide-tooltip' : bluredProp !== 'myShare'}">
            <div class="text">
              {{ errors.first('myShare') }}
            </div>
          </div>
        </div>
      </div>

      <!-- COPARENT SHARE ( SHARED ) -->
      <div class="part extra-short-right-part width-40" v-if="type === $SHARED_EXPENSE_TYPE">
        <p class="label input-filter-label">{{ $lang.messages.coparentShare }}</p>
        <div class="input-wrapper input-field" :class="{'error-input' : errors.has('coparentsShare')}">
          <input
            type="number"
            name="coparentsShare"
            class="input input-currency"
            placeholder="0"
            v-model="payment.coparentsShare"
            required
            v-validate
            onkeypress="return event.charCode >= 48 && event.charCode <= 57"
            onkeydown="return event.keyCode !== 69"
            @input="lastUpdated = 'coparentsShare'"
            @focus.self="clearInputErrors"
            @blur="clearOthers"
            min="0"
            max="100"
          />
          <span class="input-currency-sign">%</span>
          <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon">
          <div class="error-tooltip" v-show="errors.has('coparentsShare')" :class="{'hide-tooltip' : bluredProp !== 'coparentsShare'}">
            <div class="text">
              {{ errors.first('coparentsShare') }}
            </div>
          </div>
        </div>
      </div>

      <!-- SLIDER ( SHARED ) -->
      <div class="part slider-part" v-if="type === $SHARED_EXPENSE_TYPE">
        <vue-slider
          v-model="payment.coparentsShare"
          :bgStyle="bgStyle"
          :sliderStyle="sliderStyle"
          :tooltipStyle="tooltipStyle"
          :processStyle="processStyle"
          @click.native="lastUpdated = 'slider'"
          @mousedown.native="lastUpdated = 'slider'">
        </vue-slider>
      </div>

      <!-- REIMBURSEMENT AMOUNT ( SHARED ) -->
      <div class="part short-part pay-m-right-50 width-40" v-if="type === $SHARED_EXPENSE_TYPE">
        <p class="label input-filter-label">{{ $lang.messages.reimbursementAmount }}</p>
        <div
          class="input-wrapper input-field"
          :class="{'error-input' : errors.has('reimbursementAmount')}"
        >
          <input
            type="number"
            name="reimbursementAmount"
            class="input input-currency"
            placeholder="1"
            v-model="payment.reimbursementAmount"
            v-validate="'required|min_value:1|decimal:2'"
            onkeydown="return event.keyCode !== 69"
            @keypress="MX_preventCharInput"
            @paste.prevent
            @input="lastUpdated = 'reimbursementAmount'"
            @focus.self="clearInputErrors"
            @blur="clearOthers"
            min="1"
            :max="MX_maxInputValue"
          />
          <span class="input-currency-sign">
            <currency-sign :currency="currency" />
          </span>
          <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon">
          <div
            class="error-tooltip"
            v-show="errors.has('reimbursementAmount')"
            :class="{'hide-tooltip' : bluredProp !== 'reimbursementAmount' }"
          >
            <div class="text">
              {{ errors.first('reimbursementAmount') }}
            </div>
          </div>
        </div>
      </div>

      <!-- REIMBURSEMENT PAYOR ( SHARED ) -->
      <div class="part short-part width-40" v-if="type === $SHARED_EXPENSE_TYPE">
        <p class="label input-filter-label">{{ $lang.messages.reimbursementPayor }}</p>
        <div class="input-wrapper input-field">
          <input
            readonly
            type="text"
            class="input"
            v-model="printReimbursementPayor.shortenedName"
            tabindex="-1"
            :placeholder="$lang.messages.reimbAmountBy"
          />
        </div>
      </div>
  
      <!-- AUTOPAY -->
      <template v-if="QUERY_PATH">
        <div class="part middle-part autopay">
          <label class="toggle-wrapper" :class="{ 'disabled': isAutopayDisabled() }">
            <toggle-button
                :value="payment.autopay"
                :disabled="isAutopayDisabled()"
                :color="{checked: '#1554E4', unchecked: '#D1D1D1', disabled: '#CCCCCC'}"
                :switch-color="'#1554E4'"
                :width="22"
                :height="8"
                :margin="-4"
                :key="payment.autopay"
                @change="clickAutopay"
            />
            <span>{{ $lang.messages.tp_autopayTitle }}</span>
          </label>
          <span class="not-payable-reason" v-if="isAutopayDisabled()">
            {{ notPayableReason }}
          </span>
          <span class="box-name-info" @click="showTooltip(1)" :class="{ 'blue-box-info': tooltipId === 1}" />
          <f-p-tooltip
              v-if="tooltipId === 1"
              v-click-outside="hideTooltip"
              :hideTooltipInside="hideTooltip"
              :tooltipTitle="$lang.messages.tp_autopayTitle"
              :tooltipContent="$lang.messages.tp_autopayText">
          </f-p-tooltip>
        </div>
      </template>
    </section>

    <!-- NOTE & ATTACHMENT -->
    <section class="section-col">
      <!-- NOTE TEXTAREA-->
      <f-p-textarea-note
        :noteHeadline="$lang.messages.notePrivate"
        :textareaValue="payment.note"
        :noteType="$lang.messages.enterNote"
        @textareaInput="textareaInput">
      </f-p-textarea-note>

      <!-- ATTACHMENT -->
      <f-p-input-upload
        :attachments="payment.attachments"
        :uploadedAttachments="payment.expenseAttachments"
        :actionType="actionType"
        @deleteAttachment="deleteAttachment"
        @deleteUploadAttachment="deleteUploadAttachment"
        @onChangeFile="onChangeFile">
      </f-p-input-upload>
    </section>

    <!-- SCHEDULE RECURRING EXPENSE CALENDAR -->
    <template v-if="QUERY_PATH">
      <div class="section-col">
        <span class="uppercase-text">{{ $lang.messages.paymentsSchedule }}</span>
        <hr class="horizontal-line">
        <div class="calendar-area">
          <schedule-recurring-expense-calendar ref="calendar" @setCalendarAttrs="setCalendarAttrs" />
        </div>
        <hr class="horizontal-line">
        <div class="payment-list">
          <div class="payment" v-for="(item, index) in calendarAttrs[1].dates" :key="index" v-show="payment.amount > 0 || payment.reimbursementAmount > 0">
            <div class="amount">
              <span v-if="type !== $SHARED_EXPENSE_TYPE">{{ $lang.messages.amount }}: </span>
              <span v-if="type === $SHARED_EXPENSE_TYPE && payment.paidBy.guid === coparentGuid">{{ $lang.messages.myShare }}: </span>
              <span v-if="type === $SHARED_EXPENSE_TYPE && payment.paidBy.guid !== coparentGuid">{{ $lang.messages.coparentShare }}: </span>
              <span>
                <currency-sign :currency="currency" />{{ payment.amount || payment.reimbursementAmount }}
              </span>
            </div>
            <div class="date">{{ dateFormat(item) }}</div>
          </div>
          <div class="payment" v-show="payment.endUnit.value ==='never' && (payment.amount > 0 || payment.reimbursementAmount > 0)">
            <div>.</div>
            <div>.</div>
            <div>.</div>
          </div>
        </div>
      </div>
    </template>

    <!-- BUTTONS -->
    <slot name="paymentButtons" />

  </form>
</template>

<script>
  import { mapActions, mapGetters } from "vuex";
  import {
    CHILD_MAINTENANCE_ADD_NEW,
    CHILD_MAINTENANCE_EDIT,
    CHILD_MAINTENANCE_FIND_BY_GUID,
    GET_COPARENTS_CHILDREN,
    SHARED_EXPENSES_ADD_NEW,
    SHARED_EXPENSES_EDIT,
    SHARED_EXPENSES_FIND_BY_GUID,
    SPOUSAL_MAINTENANCE_ADD_NEW,
    SPOUSAL_MAINTENANCE_EDIT,
    SPOUSAL_MAINTENANCE_FIND_BY_GUID,
    CHILD_MAINTENANCE_SCHEDULED_ADD_NEW,
    SHARED_EXPENSES_SCHEDULED_ADD_NEW,
    RECURRING_EXPENSE_CM_UPDATE,
    RECURRING_EXPENSE_FIND_BY_GUID,
    RECURRING_EXPENSE_SM_UPDATE,
    RECURRING_EXPENSE_SE_UPDATE,
    SPOUSAL_MAINTENANCE_SCHEDULED_ADD_NEW,
    RECURRING_EXPENSE_INTERVAL_UNITS
  } from '@/store/action.types';
  import { dateFormat, requestDateFormat, zoneIdDateFormat, requestDateFormatCurrentTime } from '@/mixins/dateFormat';
  import { preventCharEnter } from "@/mixins/preventCharEnter";
  import { maxInputValue } from "@/mixins/maxInputValue";
  import { addMonths } from '@/mixins/addMonths';
  import { addYears } from '@/mixins/addYears';
  import Autocomplete from '@/components/common/Autocomplete';
  import CurrencySign from '@/components/common/CurrencySign';
  import Base64Upload from '@/components/common/Base64Upload';
  import ChildrenChecklist from '@/components/common/ChildrenChecklist';
  import CasesChecklist from '@/components/common/CasesChecklist';
  import FPDatepicker from '@/components/common/FPDatepicker';
  import FPInputUpload from "@/components/common/inputs/FPInputUpload";
  import FPTextareaNote from "@/components/common/inputs/FPTextareaNote";
  import ScheduleRecurringExpenseCalendar from '@/components/common/ScheduleRecurringExpenseCalendar';
  import Datepicker from "vuejs-datepicker";
  import VueSlider from "vue-slider-component";
  import ClipLoader from "vue-spinner/src/ClipLoader";
  import vClickOutside from 'v-click-outside';
  import { ToggleButton } from 'vue-js-toggle-button';
  import STATUS_CODES from "@/common/statusCodes";

  export default {
    name: "FPAddPaymentLayout",
    components: {
      Autocomplete,
      Datepicker,
      CurrencySign,
      Base64Upload,
      ChildrenChecklist,
      CasesChecklist,
      VueSlider,
      ClipLoader,
      'f-p-tooltip': () => import('@/components/common/FPTooltip'),
      FPDatepicker,
      'f-p-input-upload': FPInputUpload,
      'f-p-textarea-note': FPTextareaNote,
      ScheduleRecurringExpenseCalendar,
      ToggleButton
    },
    directives: {
      clickOutside: vClickOutside.directive
    },
    mixins: [
      dateFormat,
      requestDateFormat,
      preventCharEnter,
      requestDateFormatCurrentTime,
      maxInputValue,
      zoneIdDateFormat,
      addMonths,
      addYears,
    ],
    props: {
      //*******************************************//
      //--------------- Shared data ---------------//
      //*******************************************//
      type: {
        type: String,
        required: true,
        default: () => {
          return '';
        }
      },
      guid: {
        type: String,
        required: true,
        default: () => {
          return '';
        }
      },
      bluredProp: {
        type: String,
        required: true
      },
      //*******************************************//
      //-------------- FPAddPayment ---------------//
      //*******************************************//
      isAlreadyPaid: {
        type: Boolean,
        default: false
      },
      isLoadingPayNow: {
        type: Boolean,
        default: false
      },
      isLoadingAdd: {
        type: Boolean,
        default: false
      },
      isLoadingAlreadyPaid: {
        type: Boolean,
        default: false
      },
      payNow: {
        type: Boolean,
        default: false
      },
    },
    data() {
      return {
        //*******************************************//
        //--------------- Shared data ---------------//
        //*******************************************//
        QUERY_PATH: this.$route.query.type === 'recurring',
        printReimbursementPayor: {
          name: '',
          shortenedName: '',
          guid: '',
          canReceiveFunds: true
        },
        children: [],
        disabledDates: {},
        formSubmitted: false,
        serverErrors: false,
        actionType: 'add',
        rerender: 0,
        rerenderChecklist: 0,
        lastUpdated: '',
        bgStyle: {
          "backgroundColor": "#D1D1D1",
          "visibility": "visible",
          "width": "85%",
          "height": "5px"
        },
        sliderStyle: {
          "backgroundColor": "#1554E4",
          "width": "11px",
          "height": "11px",
          "marginTop": "2px"
        },
        tooltipStyle: {
          "visibility": "hidden"
        },
        processStyle: {
          "backgroundColor": "#1554E4",
        },
        tooltipId: 0,
        filteredCoparents: false,
        payment: {
          expenseGuid: '',
          description: '',
          caseGuid: '',
          coparentGuid: '',
          coparentName: '',
          coparentShortenedName: '',
          paidBy: {
            name: '',
            guid: ''
          },
          owedBy: {
            guid: '',
            name: ''
          },
          payor: {
            guid: '',
            name: ''
          },
          recipient: {
            guid: '',
            name: '',
            canReceiveFunds: false
          },
          cases: [],
          note: '',
          reimbursementAmount: '',
          alreadyPaid: false,
          myShare: 50,
          coparentsShare: 50,
          attachments: [],
          expenseAttachments: [],
          deletedAttachments: [],
          totalAmount: '',
          chosenChildren: [],
          date: new Date(),
          paidByBoolean: false,
          //*******************************************//
          //-------------- FPAddPayment ---------------//
          //*******************************************//
          casesExists: false,
          paymentType: '',
          //*******************************************//
          //------- FPScheduleRecurringPayment --------//
          //*******************************************//
          casesString: '',
          amount: '',
          endDate: this.dateUTC(new Date()),
          intervalUnit: {},
          frequencyOption: {},
          intervalDuration: 1,
          endUnit: {},
          endDuration: 1,
          children: [],
          autopay: true,
        },
        arrayCases: [],
        //*******************************************//
        //-------------- FPAddPayment ---------------//
        //*******************************************//
        typeable: true,
        coparentsRecipient: this.coparentsMaster,
        coparentsPayor: this.coparentsMaster,
        //*******************************************//
        //------- FPScheduleRecurringPayment --------//
        //*******************************************//
        arrForSchedulePayment: [],
        selectedValue: new Date(),
        calendarAttrs: [
          {
            dates: [
              new Date()
            ]
          },
          {
            dates: []
          }
        ],
        palceholderPaidBy: 'Total Amount Paid By',
        palceholderCases: 'Select Children',
        placeholderIntervalUnit: 'week',
        placeholderEndUnit: 'On',
        intervalUnits: [],
        frequencyOptions: [
          {
            value: 'weekly',
            name: 'Weekly'
          },
          {
            value: 'twoWeekly',
            name: 'Biweekly'
          },
          {
            value: 'monthly',
            name: 'Monthly'
          },
          {
            value: 'custom',
            name: 'Custom'
          },
        ],
        endUnits: [
          {
            value: 'on',
            name: 'On'
          },
          {
            value: 'after',
            name: 'After'
          },
          {
            value: 'never',
            name: 'Never'
          }
        ],
        loading: false,
        initLoading: false,
      }
    },
    computed: {
      //*******************************************//
      //-------------- Shared data ----------------//
      //*******************************************//
      notPayableReason() {
        if (!this.canSendFunds) {
          return this.$lang.messages.cannotSendFunds
        }

        if (this.payment.payor.guid !== '') {
          if (this.payment.payor.guid !== this.coparentGuid) {
            return this.$lang.messages.notPayor
          }
        }

        if (this.payment.recipient.guid !== '') {
          if (!this.payment.recipient.canReceiveFunds) {
            return this.$lang.messages.coparentCannotSendFunds
          }
        }

        if (this.printReimbursementPayor.guid !== '') {
          if (this.printReimbursementPayor.guid !== this.coparentGuid) {
            return this.$lang.messages.notPayor
          }
        }

        if (this.payment.owedBy.guid !== '') {
          if (!this.payment.owedBy.canReceiveFunds) {
            return this.$lang.messages.coparentCannotSendFunds
          }
        }

        if (this.isAlreadyPaid) {
          return this.$lang.messages.alreadyPaidExpense
        }

        return this.$lang.messages.fillOutForm
      },
      ...mapGetters(['coparentGuid', 'canSendFunds', 'currency']),
      coparentsMaster() {
        this.coparentsRecipient = this.$store.getters.coparents;
        this.coparentsPayor = this.$store.getters.coparents;
        return this.$store.getters.coparents;
      },
      coparentShortenedName() {
        let shortenedName = '';

        for (let i of this.coparentsMaster) {
          if (i.guid === this.coparentGuid) {
            shortenedName = i.shortenedName;
            break;
          }
        }

        return shortenedName;
      },
      coparentName() {
        let name = '';

        for (let i of this.coparentsMaster) {
          if (i.guid === this.coparentGuid) {
            name = i.name;
            break;
          }
        }

        return name;
      },
      coparents() {
        if (this.filteredCoparents) {
          let coparentsTemp = JSON.parse(JSON.stringify(this.coparentsMaster));

          const self = this;

          coparentsTemp = coparentsTemp.filter(coparent => {
            return !!(coparent.guid === self.coparentGuid || coparent.guid === self.payment.coparentGuid)
          });
          return coparentsTemp;
        } else {
          return this.coparentsMaster;
        }
      },
      cases() {
        return this.$store.getters.cases.filter(item => {
          return item.children.length > 0
        })
      },
      //*******************************************//
      //-------------- FPAddPayment ---------------//
      //*******************************************//
      year: function () {
        return this.dateContext.format('Y');
      }
    },
    watch: {
      //*******************************************//
      //-------------- Shared data ----------------//
      //*******************************************//
      'payment.coparentsShare': function(value) {
        if (value !== '') {
          this.payment.myShare = Math.round(100 - value);
          if (this.lastUpdated !== 'reimbursementAmount')
            if(this.payment.paidBy.guid === this.coparentGuid) {
              this.payment.reimbursementAmount = ((this.payment.totalAmount * value) / 100).toFixed(2)
            } else {
              this.payment.reimbursementAmount = ((this.payment.totalAmount * this.payment.myShare) / 100).toFixed(2)
            }
        }
      },
      'payment.myShare': function(value) {
        if (value !== '') {
          this.payment.coparentsShare = Math.round(100 - value);
          if (this.lastUpdated !== 'reimbursementAmount')
            if(this.payment.paidBy.guid === this.coparentGuid) {
              this.payment.reimbursementAmount = ((this.payment.totalAmount * this.payment.coparentsShare) / 100).toFixed(2)
            } else {
              this.payment.reimbursementAmount = ((this.payment.totalAmount * this.payment.myShare) / 100).toFixed(2)
            }
        }
      },
      'payment.totalAmount': function(value) {
        if (value <= 0) {
          this.payment.reimbursementAmount = ''
        }
        if (value !== '' && value > 0) {
          if (this.lastUpdated !== 'reimbursementAmount') {
            if(this.payment.paidBy.guid === this.coparentGuid) {
              this.payment.reimbursementAmount = ((value * this.payment.coparentsShare) / 100).toFixed(2);
            } else {
              this.payment.reimbursementAmount = ((value * this.payment.myShare) / 100).toFixed(2);
            }
          }
        }
      },
      'payment.reimbursementAmount': function(value) {
        if (parseInt(value, 10) > parseInt(this.payment.totalAmount, 10)) {
          this.payment.reimbursementAmount = this.payment.totalAmount
        }
        if (value !== '' && this.payment.totalAmount !== '') {
          if (this.payment.paidBy.guid === this.coparentGuid) {
            this.payment.coparentsShare = Math.round((100 * value) / this.payment.totalAmount).toFixed(2);
            this.payment.myShare = Math.round(100 - this.payment.coparentsShare).toFixed(2);
          } else {
            this.payment.myShare = Math.round((100 * value) / this.payment.totalAmount).toFixed(2);
            this.payment.coparentsShare = Math.round(100 - this.payment.myShare).toFixed(2);
          }
        }
      },
      'coparents': function() {
        this.preselectPayor();
        this.autofillCoparents();
      },
      'cases': function() {
        this.autofillCases()
      }
    },
    created() {
      //*******************************************//
      //------- FPScheduleRecurringPayment --------//
      //*******************************************//
      if (this.QUERY_PATH) {
        this.getIntervalData();
      }
    },
    mounted() {
      this.preselectPayor();
      //*******************************************//
      //-------------- Shared data ----------------//
      //*******************************************//
      this.autofillCoparents();
      this.autofillCases();
      if (this.type === this.$SHARED_EXPENSE_TYPE) {
        this.loadCoparents();
        if (this.guid !== '') {
          this.loadExpense();
        }
      }
      //*******************************************//
      //------- FPScheduleRecurringPayment --------//
      //*******************************************//
      if (this.QUERY_PATH) {
        this.$emit('actionType', 'add');
        this.$root.$on('schedule-payment', this.onAddNewFormSubmitted);

        this.payment.endUnit = this.endUnits[2];
        this.payment.frequencyOption = this.frequencyOptions[0];
        this.payment.date = this.dateUTC(this.requestDateFormatCurrentTime(new Date()));
        this.payment.endDate = this.dateUTC(this.requestDateFormat(new Date()));

        if (this.type !== this.$SHARED_EXPENSE_TYPE) {
          if (this.guid !== '') {
            this.actionType = 'edit';
            // Send the actionType via props to FPScheduleRecurringPayment
            this.$emit('actionType', 'edit');
            this.findByGuidAction(this.guid)
              .then(response => {
                this.initLoading = true;
                this.payment.expenseGuid = response.data.data.scheduledExpenseBasicResponseDto.scheduledExpenseGuid;
                this.payment.payor = response.data.data.scheduledExpenseBasicResponseDto.payor;
                this.payment.payor.shortenedName = this.generateShortenedName(this.payment.payor);
                this.payment.recipient = response.data.data.scheduledExpenseBasicResponseDto.recipient;
                this.payment.recipient.shortenedName = this.generateShortenedName(this.payment.recipient);
                this.payment.date = this.dateUTC(response.data.data.firstPaymentDate);
                this.payment.intervalUnit = this.findIntervalUnit(response.data.data.intervalUnit, response.data.data.intervalDuration).intervalUnitObject;
                this.payment.frequencyOption = this.findIntervalUnit(response.data.data.intervalUnit, response.data.data.intervalDuration).interval;
                this.payment.intervalDuration = this.findIntervalUnit(response.data.data.intervalUnit, response.data.data.intervalDuration).intervalDuration;
                this.payment.amount = response.data.data.scheduledExpenseBasicResponseDto.amount;
                this.payment.note = response.data.data.scheduledExpenseBasicResponseDto.note;
                this.payment.expenseAttachments = response.data.data.attachmentResponseDtos;
                // On edit recurring payment we want to edit only cases that are matched when user initially made that payment
                if (this.actionType === 'edit') {
                  this.arrForSchedulePayment.push(
                    response.data.data.scheduledExpenseBasicResponseDto.payor,
                    response.data.data.scheduledExpenseBasicResponseDto.recipient
                  );
                } else {
                  this.arrForSchedulePayment = []
                }
                if (response.data.data.endDate) {
                  this.payment.endDate = this.dateUTC(response.data.data.endDate);
                  this.payment.endUnit = this.endUnits[0]
                }
                if (response.data.data.numberOfOccurrences) {
                  this.payment.endDuration = response.data.data.numberOfOccurrences;
                  this.payment.endUnit = this.endUnits[1]
                  this.payment.endUnit = this.endUnits[1]
                }
                if (this.type === this.$CHILD_EXPENSE_TYPE) {
                  this.payment.chosenChildren = response.data.data.scheduledExpenseBasicResponseDto.childrenDtos;

                  this.payment.chosenChildren.forEach(element => {
                    element.clicked = true
                  });
                  this.payment.children = this.payment.chosenChildren.map((child) => child.childGuid);
                  if (this.payment.payor.guid !== this.coparentGuid) {
                    this.loadCoparents(this.payment.payor.guid)
                  }
                  if (this.payment.recipient.guid !== this.coparentGuid) {
                    this.loadCoparents(this.payment.recipient.guid)
                  }
                }
                this.payment.autopay = response.data.data.autopayEnabled;
                this.drawCalendar();
              })
              .catch(error => {
                if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                  this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
                }
              });
          }
        }
      }
      //*******************************************//
      //-------------- FPAddPayments --------------//
      //*******************************************//
      else {
        this.$root.$on('submit-payment', this.onAddNewFormSubmitted);
        this.$root.$on('already-paid', this.alreadyPaidChange);

        this.payment.date = this.requestDateFormatCurrentTime(new Date());

        if (this.type !== this.$SHARED_EXPENSE_TYPE) {
          if (this.guid !== '') {
            this.actionType = 'edit';
            this.$emit('actionType', 'edit');
            if (this.type === this.$ALIMONY_EXPENSE_TYPE) {
              this.findByGuidSpouseAction(this.guid)
                .then(response => {
                  this.payment.expenseGuid = response.data.data.guid;
                  this.payment.date = response.data.data.payments[0].paymentDate;
                  this.payment.amount = response.data.data.payments[0].amount;
                  this.payment.note = response.data.data.note;
                  this.payment.payor = response.data.data.payments[0].payor;
                  this.payment.payor.shortenedName = this.generateShortenedName(this.payment.payor);
                  this.payment.recipient = response.data.data.payments[0].recipient;
                  this.payment.recipient.shortenedName = this.generateShortenedName(this.payment.recipient);
                  this.payment.alreadyPaid = response.data.data.alreadyPaid;
                  this.$emit('alreadyPaid', this.payment.alreadyPaid);
                  this.payment.expenseAttachments = response.data.data.attachments;
                })
                .catch(error => {
                  if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                    this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
                  }
                })
                .finally(() => {
                  this.isPayDisabled();
                });
            } else if (this.type === this.$CHILD_EXPENSE_TYPE) {
              this.findByGuidChildAction(this.guid)
                .then(response => {
                  this.initLoading = true;
                  this.payment.expenseGuid = response.data.data.guid;
                  this.payment.date = response.data.data.payments[0].paymentDate;
                  this.payment.amount = response.data.data.payments[0].amount;
                  this.payment.note = response.data.data.note;
                  this.payment.payor = response.data.data.payments[0].payor;
                  this.payment.payor.shortenedName = this.generateShortenedName(this.payment.payor);
                  this.payment.recipient = response.data.data.payments[0].recipient;
                  this.payment.recipient.shortenedName = this.generateShortenedName(this.payment.recipient);
                  this.payment.chosenChildren = response.data.data.children;
                  this.payment.alreadyPaid = response.data.data.alreadyPaid;
                  this.$emit('alreadyPaid', this.payment.alreadyPaid);
                  this.payment.expenseAttachments = response.data.data.attachments;
                  this.payment.chosenChildren.forEach(element => {
                    element.clicked = true
                  });
                  if (this.payment.payor.guid !== this.coparentGuid) {
                    this.loadCoparents(this.payment.payor.guid)
                  }
                  if (this.payment.recipient.guid !== this.coparentGuid) {
                    this.loadCoparents(this.payment.recipient.guid)
                  }
                  this.rerenderChecklist+=1;
                })
                .catch(error => {
                  if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                    this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
                  }
                })
                .finally(() => {
                  this.isPayDisabled();
                });
            }
          }
        }
      }
      this.isPayDisabled();
    },
    destroyed() {
      //*******************************************//
      //-------------- FPAddPayments --------------//
      //*******************************************//
      if (!this.QUERY_PATH) {
        this.$root.$off('submit-payment', this.onAddNewFormSubmitted);
        this.$root.$off('already-paid', this.alreadyPaidChange);
      } else {
        this.$root.$off('schedule-payment', this.onAddNewFormSubmitted);
      }
    },
    methods: {
      ...mapActions({
        //*******************************************//
        //--------------- Shared data ---------------//
        //*******************************************//
        getCoparentsAction: GET_COPARENTS_CHILDREN,

        //*******************************************//
        //-------------- FPAddPayments --------------//
        //*******************************************//
        addNewSpouseAction: SPOUSAL_MAINTENANCE_ADD_NEW,
        addNewChildAction: CHILD_MAINTENANCE_ADD_NEW,
        addNewSharedAction: SHARED_EXPENSES_ADD_NEW,
        findByGuidSpouseAction: SPOUSAL_MAINTENANCE_FIND_BY_GUID,
        findByGuidChildAction: CHILD_MAINTENANCE_FIND_BY_GUID,
        findByGuidSharedAction: SHARED_EXPENSES_FIND_BY_GUID,
        editSpouseAction: SPOUSAL_MAINTENANCE_EDIT,
        editChildAction: CHILD_MAINTENANCE_EDIT,
        editSharedAction: SHARED_EXPENSES_EDIT,

        //*******************************************//
        //------- FPScheduleRecurringPayments -------//
        //*******************************************//
        getInterval: RECURRING_EXPENSE_INTERVAL_UNITS,
        addNewRecSpouseAction: SPOUSAL_MAINTENANCE_SCHEDULED_ADD_NEW,
        addNewRecChildAction: CHILD_MAINTENANCE_SCHEDULED_ADD_NEW,
        addNewRecSharedAction: SHARED_EXPENSES_SCHEDULED_ADD_NEW,
        findByGuidAction: RECURRING_EXPENSE_FIND_BY_GUID,
        editRecSpouseAction: RECURRING_EXPENSE_SM_UPDATE,
        editRecChildAction: RECURRING_EXPENSE_CM_UPDATE,
        editRecSharedAction: RECURRING_EXPENSE_SE_UPDATE,
      }),
      //*******************************************//
      //--------------- Shared data ---------------//
      //*******************************************//
      clearInputErrors (e) {
        this.$validator.errors.remove(e.target.name);
        this.$emit('clearInputErrorsPayments', e);
      },
      clearOthers (e) {
        this.$emit('clearOthersPayments', e)
      },
      findCoparentMe() {
        return this.coparents.find( elem => {
          return elem.guid === this.coparentGuid
        })
      },
      findCoparentOther(val) {
        if (this.cases.length === 1 && this.cases[0].children.length === 1) {
          return this.coparents.find(elem => {
            return elem.guid === this.cases[0].coparentGuid;
          });
        } else {
          return this.coparents.find(elem => {
            return elem.guid !== val.guid;
          });
        }
      },
      // TODO: Refactor this function (similar to onSelectedRecipient)
      onSelectedPayor(value) {
        let fieldValid = false;
        for (let element of this.coparents) {
          if (element.guid === value.guid) {
            fieldValid = true;
            break;
          }
        }

        if (fieldValid) {
          this.payment.payor = value;
          if (this.payment.payor.guid !== this.coparentGuid) {
            this.payment.recipient = this.findCoparentMe();
            this.rerender = this.rerender++;
            if (this.type === this.$CHILD_EXPENSE_TYPE) {
              if (this.coparents.length === 2) {
                this.initLoading = true;
                return;
              }

              this.payment.chosenChildren = [];
              this.children = [];
              this.loadCoparents(this.payment.payor.guid);
              this.rerenderChecklist+=1;

              //*******************************************//
              //------- FPScheduleRecurringPayments -------//
              //*******************************************//
              if (this.QUERY_PATH) {
                this.payment.children = [];
              }
            }
          }
          else if (this.coparents.length === 2) {
            this.payment.recipient = this.findCoparentOther(value);
            this.initLoading = true;

            this.rerender++;
          }
        } else {
          this.payment.payor = {};
          if (this.type === this.$CHILD_EXPENSE_TYPE) {
            this.rerenderChecklist+=1;
            this.payment.chosenChildren = [];
            //*******************************************//
            //------- FPScheduleRecurringPayments -------//
            //*******************************************//
            if (this.QUERY_PATH) {
              this.payment.children = [];
              this.children = [];
            }
          }
        }
        //*******************************************//
        //-------------- FPAddPayments --------------//
        //*******************************************//
        if (!this.QUERY_PATH) {
          this.isPayDisabled();
        }
      },
      // TODO: Refactor this function (similar to onSelectedPayor)
      onSelectedRecipient(value) {
        let fieldValid = false;
        //*******************************************//
        //------- FPScheduleRecurringPayments -------//
        //*******************************************//
        if (this.QUERY_PATH) {
          this.coparents.forEach(element => {
            if (element.name === value.name) {
              fieldValid = true
            }
          });
        }
        //*******************************************//
        //-------------- FPAddPayments --------------//
        //*******************************************//
        else {
          this.coparentsRecipient.forEach(element => {
            if (element.name === value.name) {
              fieldValid = true
            }
          });
        }

        if (fieldValid) {
          this.payment.recipient = value;
          if (this.payment.recipient.guid !== this.coparentGuid) {
            this.payment.payor = this.findCoparentMe();
            this.rerender = this.rerender++;
            if (this.type === this.$CHILD_EXPENSE_TYPE) {
              if (!this.initLoading) {
                this.payment.chosenChildren = [];
              }
              else {
                this.initLoading = false;
              }
              this.children = [];
              this.loadCoparents(this.payment.recipient.guid);
              this.rerenderChecklist+=1;
              //*******************************************//
              //------- FPScheduleRecurringPayments -------//
              //*******************************************//
              if (this.QUERY_PATH) {
                if (!this.initLoading) {
                  this.payment.children = [];
                }
                else {
                  this.initLoading = false;
                }
              }
            }
          }
        } else {
          this.payment.recipient = {};
          if (this.type === this.$CHILD_EXPENSE_TYPE) {
            this.rerenderChecklist+=1;
            this.payment.chosenChildren = [];
            //*******************************************//
            //------- FPScheduleRecurringPayments -------//
            //*******************************************//
            if (this.QUERY_PATH) {
              this.payment.children = [];
              this.children = [];
            }
          }
        }
        //*******************************************//
        //-------------- FPAddPayments --------------//
        //*******************************************//
        if (!this.QUERY_PATH) {
          this.isPayDisabled();
        }
      },
      onSelectedPaymentDate (value) {
        this.payment.date = new Date(value);
        //*******************************************//
        //------- FPScheduleRecurringPayments -------//
        //*******************************************//
        if (this.QUERY_PATH) {
          if (new Date(this.payment.date) > new Date(this.payment.endDate)) {
            this.onSelectedEndDate(this.payment.date);
          }
          this.drawCalendar();
        }
      },
      onSelectedChildren(results) {
        //*******************************************//
        //------- FPScheduleRecurringPayments -------//
        //*******************************************//
        if (this.QUERY_PATH) {
          this.payment.children = [];
          results.forEach(element => {
            if (element.clicked === true)
              this.payment.children.push(element.childGuid)
          })
        }
        //*******************************************//
        //-------------- FPAddPayments --------------//
        //*******************************************//
        else {
          this.payment.chosenChildren = [];
          results.forEach(element => {
            if (element.clicked === true)
              this.payment.chosenChildren.push(element)
          })
        }
      },
      onSelectedCases(results) {
        let somethingClicked = false;
        this.payment.cases = [];
        //*******************************************//
        //------- FPScheduleRecurringPayments -------//
        //*******************************************//
        if (this.QUERY_PATH) {
          this.payment.casesString = '';
        }
        //*******************************************//
        //--------------- Shared data ---------------//
        //*******************************************//
        this.arrayCases = [];
        this.arrayCases.push(this.findCoparentMe());
        results.forEach(element => {
          if (element.children) {
            element.children.forEach(child => {
              if (child.clicked) {
                if (this.arrayCases.indexOf(element) === -1) {
                  this.arrayCases.push(element);
                }
                //*******************************************//
                //------- FPScheduleRecurringPayments -------//
                //*******************************************//
                if (this.QUERY_PATH) {
                  this.payment.cases.push(child.childGuid);
                }
                //*******************************************//
                //-------------- FPAddPayments --------------//
                //*******************************************//
                else {
                  this.payment.cases.push(child);
                }
                this.payment.caseGuid = element.caseGuid;
                this.payment.coparentGuid = element.coparentGuid;
                this.payment.coparentName = element.coparentName;
                this.payment.coparentShortenedName = element.shortenedName;
                //*******************************************//
                //------- FPScheduleRecurringPayments -------//
                //*******************************************//
                if (this.QUERY_PATH && !this.payment.casesString.includes(child.name)) {
                  this.payment.casesString = this.payment.casesString + ',' + child.name
                }
                somethingClicked = true;
              }
            })
          }
        });

        //*******************************************//
        //------- FPScheduleRecurringPayments -------//
        //*******************************************//
        if (this.QUERY_PATH) {
          if (this.payment.casesString.substring(0, 1) === ',') {
            this.payment.casesString = this.payment.casesString.substring(1, this.payment.casesString.length)
          }
          if (this.payment.casesString.substring(this.payment.casesString.length - 1, this.payment.casesString.length) === ",") {
            this.payment.casesString = this.payment.casesString.substring(0, this.payment.casesString.length - 1)
          }
        }

        //*******************************************//
        //--------------- Shared data ---------------//
        //*******************************************//
        if (somethingClicked) {
          this.generateFilteredCoparents()
        } else {
          this.loadCoparents()
        }

        //*******************************************//
        //-------------- FPAddPayments --------------//
        //*******************************************//
        if (!this.QUERY_PATH) {
          this.isPayDisabled();
        }
      },
      generateFilteredCoparents() {
        this.filteredCoparents = true;
        this.rerender+=1;
        // DISABLED FOR TESTING
        //*******************************************//
        //------- FPScheduleRecurringPayments -------//
        //*******************************************//
        // if (this.QUERY_PATH) {
        //   if (!this.coparents.includes(this.payment.paidBy)) {
        //     this.payment.paidBy.name = '';
        //     this.payment.paidBy.guid = ''
        //   }
        // }
        //*******************************************//
        //-------------- FPAddPayments --------------//
        //*******************************************//
        // else {
        //   if (this.coparents.includes(this.payment.paidBy) === false && this.actionType === 'add') {
        //     this.payment.paidBy.name = '';
        //     this.payment.paidBy.guid = '';
        //     this.rerender = this.rerender + 1;
        //   }
        // }
      },
      onSelectedPaidBy(value) {
        this.payment.paidBy = value;
        // Based on value inside input with title "Originally paid by"
        // print the reimbursement payor
        this.reimbursementPayorName()
      },
      calculateReimbursementAmount() {
        if (this.payment.totalAmount !== '') {
          if(this.payment.paidBy.name) {
            if(this.payment.paidBy.guid === this.coparentGuid) {
              if (this.lastUpdated !== 'reimbursementAmount')
                this.payment.reimbursementAmount = ((this.payment.totalAmount * this.payment.coparentsShare) / 100).toFixed(2)
            } else {
              if (this.lastUpdated !== 'reimbursementAmount')
                this.payment.reimbursementAmount = ((this.payment.totalAmount * this.payment.myShare) / 100).toFixed(2)
            }
          }
        }
      },
      loadCoparents(guid) {
        if (this.type === this.$CHILD_EXPENSE_TYPE) {
          this.getCoparentsAction(guid)
            .then(response => {
              response.data.data.forEach(element => {
                element.clicked = false;
                let find = false;
                for (let i of this.children) {
                  if(i.childGuid === element.childGuid) {
                    find = true;
                    break;
                  }
                }
                if (!find) {
                  this.children.push(element)
                }
              });

              this.rerenderChecklist+=1;

              this.autofillChildren();
            })
            .catch(error => {
              if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
              }
            })
        }
      },
      loadExpense() {
        this.actionType = 'edit';
        //*******************************************//
        //------- FPScheduleRecurringPayments -------//
        //*******************************************//
        if (this.QUERY_PATH) {
          this.$emit('actionType', 'edit');
          this.findByGuidAction(this.guid)
            .then(response => {
              this.payment.expenseGuid = response.data.data.scheduledExpenseBasicResponseDto.scheduledExpenseGuid;
              this.payment.description = response.data.data.scheduledExpenseBasicResponseDto.description;
              this.payment.date = this.dateUTC(response.data.data.firstPaymentDate);
              this.payment.intervalUnit = this.findIntervalUnit(response.data.data.intervalUnit, response.data.data.intervalDuration).intervalUnitObject;
              this.payment.intervalDuration = this.findIntervalUnit(response.data.data.intervalUnit, response.data.data.intervalDuration).intervalDuration;
              this.payment.frequencyOption = this.findIntervalUnit(response.data.data.intervalUnit, response.data.data.intervalDuration).interval;
              this.payment.totalAmount = response.data.data.scheduledExpenseBasicResponseDto.amount;
              this.payment.paidBy = response.data.data.scheduledExpenseBasicResponseDto.payor;
              this.payment.paidBy.shortenedName = this.generateShortenedName(this.payment.paidBy);
              this.payment.paidByBoolean = true;
              this.payment.reimbursementAmount = response.data.data.scheduledExpenseBasicResponseDto.reimbursementAmount;
              this.lastUpdated = 'reimbursementAmount';
              this.payment.coparentsShare = Math.round(((100 * this.payment.reimbursementAmount) / this.payment.totalAmount) * 100) / 100;
              this.payment.myShare = Math.round((100 - this.payment.coparentsShare) * 100) / 100;
              this.payment.recipientGuid = response.data.data.scheduledExpenseBasicResponseDto.recipient.guid;
              this.payment.note = response.data.data.scheduledExpenseBasicResponseDto.note;
              this.payment.expenseAttachments = response.data.data.attachmentResponseDtos;
              this.payment.autopay = response.data.data.autopayEnabled;
              if (response.data.data.endDate) {
                this.payment.endDate = this.dateUTC(response.data.data.scheduledExpenseBasicResponseDto.endDate);
                this.payment.endUnit = this.endUnits[0]
              }
              if (response.data.data.numberOfOccurrences) {
                this.payment.endDuration = response.data.data.numberOfOccurrences;
                this.payment.endUnit = this.endUnits[1]
              }
              response.data.data.scheduledExpenseBasicResponseDto.childrenDtos.forEach(child => {
                this.cases.forEach(caseIt => {
                  let caseChildren = caseIt.children;
                  caseChildren.forEach(childIt => {
                    if (child.childGuid === childIt.childGuid) {
                      childIt.clicked = true;
                      this.payment.cases.push(childIt.childGuid);
                      this.payment.coparentGuid = caseIt.coparentGuid;
                      this.payment.coparentName = caseIt.coparentName;
                      this.payment.coparentShortenedName = caseIt.shortenedName;
                    }
                  })
                })
              });
              this.payment.casesExists = true;
              this.reimbursementPayorName();
              this.drawCalendar();
            })
            .catch(error => {
              if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
              }
            });
        }
        //*******************************************//
        //-------------- FPAddPayments --------------//
        //*******************************************//
        else {
          this.findByGuidSharedAction(this.guid)
            .then(response => {
              this.payment.alreadyPaid = response.data.data.alreadyPaid;
              this.$emit('alreadyPaid', this.payment.alreadyPaid);
              this.payment.expenseGuid = response.data.data.guid;
              this.payment.description = response.data.data.description;
              this.payment.expenseAttachments = response.data.data.attachments;
              response.data.data.payments.forEach(payment => {
                if (payment.paymentType === 'Original payment') {
                  this.payment.date = payment.paymentDate;
                  this.payment.paidBy = payment.payor;
                  this.payment.paidByBoolean = true;
                  this.payment.totalAmount = payment.amount;
                } else if (payment.paymentType === 'Reimbursement payment') {
                  this.payment.reimbursementAmount = payment.reimbursementAmount;
                  this.lastUpdated = 'reimbursementAmount';
                }
              });

              this.payment.note = response.data.data.note;

              this.payment.coparentsShare = Math.round((100 - this.payment.coparentsShare) * 100) / 100;
              this.payment.myShare = Math.round(((100 * this.payment.reimbursementAmount) / this.payment.totalAmount) * 100) / 100;

              response.data.data.children.forEach(child => {
                this.cases.forEach(caseIt => {
                  let caseChildren = caseIt.children;
                  caseChildren.forEach(childIt => {
                    if (child.childGuid === childIt.childGuid) {
                      childIt.clicked = true;
                      this.payment.cases.push(childIt);
                      this.payment.coparentGuid = caseIt.coparentGuid;
                      this.payment.coparentName = caseIt.coparentName;
                      this.payment.coparentShortenedName = caseIt.shortenedName;
                    }
                  })
                })
              });
              this.payment.casesExists = true;
              this.generateFilteredCoparents();
              this.reimbursementPayorName()
            })
            .catch(error => {
              if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
              }
            })
            .finally(() => this.isPayDisabled());
        }
      },
      onAddNewFormSubmitted() {
        this.$emit('checkAnyInput')
        this.$validator.validateAll()
          .then(result => {
            this.formSubmitted = true;
            let payorValid = null;
            let recipientValid = null;
            let childrenValid = null;
            let paidByValid = null;
            let casesValid = null;

            if (this.type !== this.$SHARED_EXPENSE_TYPE) {
              payorValid = this.checkCoparentsValidity(this.payment.payor.name);
              recipientValid = this.checkCoparentsValidity(this.payment.recipient.name);
            }

            if (this.type === this.$CHILD_EXPENSE_TYPE) {
              //*******************************************//
              //------- FPScheduleRecurringPayments -------//
              //*******************************************//
              if (this.QUERY_PATH) {
                childrenValid = this.checkChildrenValidity(this.payment.children);
              }
              //*******************************************//
              //-------------- FPAddPayments --------------//
              //*******************************************//
              else {
                childrenValid = this.checkChildrenValidity(this.payment.chosenChildren);
              }
            }

            if (this.type === this.$SHARED_EXPENSE_TYPE) {
              paidByValid = this.checkCoparentsValidity(this.payment.paidBy.name);
              casesValid = this.checkCasesValidity(this.payment.cases);
            }

            if (payorValid === false && this.type !== this.$SHARED_EXPENSE_TYPE) {
              this.$validator.errors.add({
                field: "payor",
                msg: `${this.$lang.vee_selectValidField} ${this.$lang.messages.payor.toLowerCase()}.`
              });
              this.$refs.autocompletePayor.setValidateField()
            }

            if (recipientValid === false && this.type !== this.$SHARED_EXPENSE_TYPE) {
              this.$validator.errors.add({
                field: "recipient",
                msg: `${this.$lang.vee_selectValidField} ${this.$lang.messages.recipient.toLowerCase()}.`
              });
              this.$refs.autocompleteRecipient.setValidateField()
            }

            if (childrenValid === false && this.type === this.$CHILD_EXPENSE_TYPE) {
              this.$validator.errors.add({
                field: "children",
                msg: `${this.$lang.vee_selectValidField} ${this.$lang.messages.children.toLowerCase()}.`
              });
              this.$refs.childrenChecklist.setValidateField()
            }

            if (paidByValid === false && this.type === this.$SHARED_EXPENSE_TYPE) {
              //*******************************************//
              //------- FPScheduleRecurringPayments -------//
              //*******************************************//
              if (this.QUERY_PATH) {
                this.$validator.errors.add({
                  field: "paidBy",
                  msg: `${this.$lang.vee_selectValidField} ${this.$lang.messages.payor.toLowerCase()}.`
                });
              }
              //*******************************************//
              //-------------- FPAddPayments --------------//
              //*******************************************//
              else {
                this.$validator.errors.add({
                  field: "paidBy",
                  msg: `${this.$lang.vee_selectValidField} ${this.$lang.messages.coparent}.`
                });
              }
              this.$refs.autocompletePaidBy.setValidateField()
            }

            if (casesValid === false && this.type === this.$SHARED_EXPENSE_TYPE) {
              this.$validator.errors.add({
                field: "cases",
                msg: `${this.$lang.vee_selectValidField} ${this.$lang.messages.children.toLowerCase()}.`
              });
              //*******************************************//
              //--------------- Shared data ---------------//
              //*******************************************//
              this.$refs.childrenCasesChecklist.setValidateField()
            }

            if (result && payorValid && recipientValid && this.type === this.$ALIMONY_EXPENSE_TYPE) {
              //*******************************************//
              //------- FPScheduleRecurringPayments -------//
              //*******************************************//
              if (this.QUERY_PATH) {
                if (this.actionType === "edit") {
                  this.editPayment()
                } else if (this.actionType === "add") {
                  this.addPayment()
                }
              }
              //*******************************************//
              //-------------- FPAddPayments --------------//
              //*******************************************//
              else {
                if (this.actionType === "edit" && !this.payNow) {
                  this.editPayment()
                } else if (this.actionType === "edit" && this.payNow) {
                  this.editPayment(true)
                } else if (this.actionType === "add" && !this.payNow) {
                  this.addPayment(false)
                } else if (this.actionType === "add" && this.payNow) {
                  this.addPayment(true)
                }
              }
            }

            if (result && payorValid && recipientValid && childrenValid && this.type === this.$CHILD_EXPENSE_TYPE) {
              //*******************************************//
              //------- FPScheduleRecurringPayments -------//
              //*******************************************//
              if (this.QUERY_PATH) {
                if (this.actionType === "edit") {
                  this.editPayment()
                } else if (this.actionType === "add") {
                  this.addPayment()
                }
              }
              //*******************************************//
              //-------------- FPAddPayments --------------//
              //*******************************************//
              else {
                if (this.actionType === "edit" && !this.payNow) {
                  this.editPayment()
                } else if (this.actionType === "edit" && this.payNow) {
                  this.editPayment(true)
                } else if (this.actionType === "add" && !this.payNow) {
                  this.addPayment(false)
                } else if (this.actionType === "add" && this.payNow) {
                  this.addPayment(true)
                }
              }
            }

            if (result && paidByValid && casesValid && this.type === this.$SHARED_EXPENSE_TYPE) {
              //*******************************************//
              //------- FPScheduleRecurringPayments -------//
              //*******************************************//
              if (this.QUERY_PATH) {
                if (this.payment.paidBy.guid !== this.coparentGuid) {
                  let coparentMe = this.findCoparentMe();
                  this.payment.coparentGuid = coparentMe.guid
                }

                if (this.actionType === "edit") {
                  this.editPayment()
                } else if (this.actionType === "add") {
                  this.addPayment()
                }
              }
              //*******************************************//
              //-------------- FPAddPayments --------------//
              //*******************************************//
              else {
                if (this.actionType === "edit" && !this.payNow) {
                  this.editPayment()
                } else if (this.actionType === "edit" && this.payNow) {
                  this.editPayment(true)
                } else if (this.actionType === "add" && !this.payNow) {
                  this.addPayment(false)
                } else if (this.actionType === "add" && this.payNow) {
                  this.addPayment(true)
                }
              }
            }

            if(!result) {
              this.$emit('alreadyPaid', false);
            }
          })
          .catch(error => {
            if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
              this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
            }
          })
      },
      checkCoparentsValidity(name) {
        let valid = false;
        this.coparents.forEach(element => {
          if (element.name === name) {
            valid = true
          }
        });
        return valid
      },
      checkCasesValidity(input) {
        let cases = [];
        if (!input.length) {
          return false
        }
        let allChildren = this.cases.reduce((acc, caseIterator) => acc.concat(caseIterator.children), []).map(child => child.childGuid);
        //*******************************************//
        //-------------- FPAddPayments --------------//
        //*******************************************//
        if (!this.QUERY_PATH) {
          input = input.map(child => child.childGuid);
        }
        for (let x = 0; x < input.length; x++) {
          cases.push(allChildren.includes(input[x].childGuid))
        }
        return cases
        // return input.every(item => allChildren.includes(item))
      },
      checkChildrenValidity(children) {
        let valid = false;
        children.forEach(element => {
          valid = this.checkChildValidity(element)
        });
        return valid
      },
      checkChildValidity(element) {
        let valid = false;
        this.children.forEach(child => {
          //*******************************************//
          //------- FPScheduleRecurringPayments -------//
          //*******************************************//
          if (this.QUERY_PATH) {
            if (child.childGuid === element) {
              valid = true
            }
          }
          //*******************************************//
          //-------------- FPAddPayments --------------//
          //*******************************************//
          else {
            if (child.childGuid === element.childGuid) {
              valid = true
            }
          }
        });
        return valid
      },
      editPayment(isQuery = false) {
        //*******************************************//
        //------- FPScheduleRecurringPayments -------//
        //*******************************************//
        if (this.QUERY_PATH) {
          if (!this.loading) {
            this.loading = true;
            this.$emit('isLoading', true);
            if (this.type === this.$ALIMONY_EXPENSE_TYPE) {
              this.editRecSpouseAction({
                expenseRequestDto: {
                  expenseGuid: this.payment.expenseGuid,
                  payorGuid: this.payment.payor.guid,
                  recipientGuid: this.payment.recipient.guid,
                  amount: this.payment.amount,
                  note: this.payment.note === '' ? null : this.payment.note,
                  zoneId: this.zoneIdDateFormat(this.payment.date),
                },
                firstPaymentDate: this.requestDateFormat(this.payment.date),
                intervalDuration: this.findInterval(this.payment.frequencyOption, this.payment.intervalUnit, this.payment.intervalDuration).intervalDuration,
                intervalUnit: this.findInterval(this.payment.frequencyOption, this.payment.intervalUnit, this.payment.intervalDuration).intervalUnit,
                endDate: this.payment.endUnit.value === 'never' || this.payment.endUnit.value === 'after' ? null : this.requestDateFormat(this.payment.endDate),
                numberOfOccurrences: this.payment.endUnit.value === 'after' ? this.payment.endDuration : null,
                basicAttachmentRequestDtos: this.payment.attachments,
                attachmentGuidsForDeletion: this.payment.deletedAttachments,
                autopayEnabled: this.payment.autopay
              })
                .then(response => {
                  this.serverErrors = false;
                  this.$snotify.success(this.$lang.messages.sn_paymentSuccesSaved, 'SUCCESS');
                  this.loading = false;
                  this.$emit('isLoading', false);
                  this.$router.push({
                    name: "SpousalMaintenanceRecurringExpensesDetails",
                    params: {id: response.data.data.expenseRequestDto.expenseGuid}
                  })
                })
                .catch(error => {
                  this.loading = false;
                  this.$emit('isLoading', false);
                  if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                    this.$snotify.error(error.response.data.status.desc, 'ERROR')
                  }
                });
            } else if (this.type === this.$CHILD_EXPENSE_TYPE) {
              this.editRecChildAction({
                expenseRequestDto: {
                  expenseGuid: this.payment.expenseGuid,
                  payorGuid: this.payment.payor.guid,
                  recipientGuid: this.payment.recipient.guid,
                  amount: this.payment.amount,
                  note: this.payment.note === '' ? null : this.payment.note,
                  zoneId: this.zoneIdDateFormat(this.payment.date),
                },
                firstPaymentDate: this.requestDateFormat(this.payment.date),
                intervalDuration: this.findInterval(this.payment.frequencyOption, this.payment.intervalUnit, this.payment.intervalDuration).intervalDuration,
                intervalUnit: this.findInterval(this.payment.frequencyOption, this.payment.intervalUnit, this.payment.intervalDuration).intervalUnit,
                endDate: this.payment.endUnit.value === 'never' || this.payment.endUnit.value === 'after' ? null : this.requestDateFormat(this.payment.endDate),
                numberOfOccurrences: this.payment.endUnit.value === 'after' ? this.payment.endDuration : null,
                childrenGuid: this.payment.children,
                basicAttachmentRequestDtos: this.payment.attachments,
                attachmentGuidsForDeletion: this.payment.deletedAttachments,
                autopayEnabled: this.payment.autopay
              })
                .then(response => {
                  this.serverErrors = false;
                  this.$snotify.success(this.$lang.messages.sn_paymentSuccesSaved, 'SUCCESS');
                  this.loading = false;
                  this.$emit('isLoading', false);
                  this.$router.push({
                    name: "ChildMaintenanceRecurringExpensesDetails",
                    params: {id: response.data.data.expenseRequestDto.expenseGuid}
                  })
                })
                .catch(error => {
                  this.loading = false;
                  this.$emit('isLoading', false);
                  if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                    this.$snotify.error(error.response.data.status.desc, 'ERROR')
                  }
                });
            } else if (this.type === this.$SHARED_EXPENSE_TYPE) {
              this.editRecSharedAction({
                description: this.payment.description,
                expenseRequestDto: {
                  expenseGuid: this.payment.expenseGuid,
                  payorGuid: this.payment.paidBy.guid,
                  recipientGuid: this.payment.coparentGuid,
                  amount: this.payment.totalAmount,
                  note: this.payment.note === '' ? null : this.payment.note,
                  zoneId: this.zoneIdDateFormat(this.payment.date),
                },
                firstPaymentDate: this.requestDateFormat(this.payment.date),
                intervalDuration: this.findInterval(this.payment.frequencyOption, this.payment.intervalUnit, this.payment.intervalDuration).intervalDuration,
                intervalUnit: this.findInterval(this.payment.frequencyOption, this.payment.intervalUnit, this.payment.intervalDuration).intervalUnit,
                endDate: this.payment.endUnit.value === 'never' || this.payment.endUnit.value === 'after' ? null : this.requestDateFormat(this.payment.endDate),
                numberOfOccurrences: this.payment.endUnit.value === 'after' ? this.payment.endDuration : null,
                childrenGuids: this.payment.cases,
                reimbursementAmount: this.payment.reimbursementAmount,
                basicAttachmentRequestDtos: this.payment.attachments,
                attachmentGuidsForDeletion: this.payment.deletedAttachments,
                autopayEnabled: this.payment.autopay
              })
                .then(response => {
                  this.serverErrors = false;
                  this.$snotify.success(this.$lang.messages.sn_paymentSuccesSaved, 'SUCCESS');
                  this.loading = false;
                  this.$emit('isLoading', false);
                  this.$router.push({
                    name: "SharedExpensesRecurringExpensesDetails",
                    params: {id: response.data.data.expenseRequestDto.expenseGuid}
                  })
                })
                .catch(error => {
                  this.loading = false;
                  this.$emit('isLoading', false);
                  if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                    this.$snotify.error(error.response.data.status.desc, 'ERROR')
                  }
                });
            }
          }
        }
        //*******************************************//
        //-------------- FPAddPayments --------------//
        //*******************************************//
        else {
          if ((this.payNow && !this.isLoadingPayNow) || (!this.payNow && !this.isLoadingAdd && !this.isLoadingAlreadyPaid)) {
            if (this.payNow) {
              this.$emit('loadingPayNow', true)
            } else if(!this.isAlreadyPaid) {
              this.$emit('loadingAdd', true)
            } else {
              this.$emit('loadingAlreadyPaid', true)
            }
            if (this.type === this.$ALIMONY_EXPENSE_TYPE) {
              this.editSpouseAction({
                expenseRequestDto: {
                  expenseGuid: this.payment.expenseGuid,
                  payorGuid: this.payment.payor.guid,
                  recipientGuid: this.payment.recipient.guid,
                  amount: this.payment.amount,
                  note: this.payment.note === '' ? null : this.payment.note
                },
                paymentDate: this.requestDateFormat(this.payment.date),
                alreadyPaid: this.payment.alreadyPaid,
                attachments: this.payment.attachments,
                attachmentGuidsForDeletion: this.payment.deletedAttachments
              })
                .then(response => {
                  this.serverErrors = false;
                  this.$snotify.success(this.$lang.messages.sn_paymentSuccesSaved, 'SUCCESS');
                  this.$emit('loadingPayNow', false);
                  this.$emit('loadingAdd', false);
                  this.$emit('loadingAlreadyPaid', false);

                  if (isQuery) {
                    this.$router.push({
                      name: "ExpenseDetailsSpousalMaintenance",
                      params: {id: response.data.data.guid},
                      query: {pay: true}
                    })
                  } else {
                    this.$router.push({
                      name: "ExpenseDetailsSpousalMaintenance",
                      params: {id: response.data.data.guid}
                    })
                  }
                })
                .catch(error => {
                  this.$emit('loadingPayNow', false);
                  this.$emit('loadingAdd', false);
                  this.$emit('loadingAlreadyPaid', false);
                  if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                    this.$snotify.error(error.response.data.status.desc, 'ERROR')
                  }
                });
            } else if (this.type === this.$CHILD_EXPENSE_TYPE) {
              this.editChildAction({
                expenseRequestDto: {
                  expenseGuid: this.payment.expenseGuid,
                  payorGuid: this.payment.payor.guid,
                  recipientGuid: this.payment.recipient.guid,
                  amount: this.payment.amount,
                  note: this.payment.note === '' ? null : this.payment.note
                },
                children: this.payment.chosenChildren.map((child) => child.childGuid),
                paymentDate: this.requestDateFormat(this.payment.date),
                alreadyPaid: this.payment.alreadyPaid,
                attachments: this.payment.attachments,
                attachmentGuidsForDeletion: this.payment.deletedAttachments
              })
                .then(response => {
                  this.serverErrors = false;
                  this.$snotify.success(this.$lang.messages.sn_paymentSuccesSaved, 'SUCCESS');
                  this.$emit('loadingPayNow', false);
                  this.$emit('loadingAdd', false);
                  this.$emit('loadingAlreadyPaid', false);

                  if (isQuery) {
                    this.$router.push({
                      name: "ExpenseDetailsChildMaintenance",
                      params: {id: response.data.data.guid},
                      query: {pay: true}
                    })
                  } else {
                    this.$router.push({
                      name: "ExpenseDetailsChildMaintenance",
                      params: {id: response.data.data.guid}
                    })
                  }
                })
                .catch(error => {
                  this.$emit('loadingPayNow', false);
                  this.$emit('loadingAdd', false);
                  this.$emit('loadingAlreadyPaid', false);
                  if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                    this.$snotify.error(error.response.data.status.desc, 'ERROR')
                  }
                });
            } else if (this.type === this.$SHARED_EXPENSE_TYPE) {
              this.editSharedAction({
                expenseGuid: this.payment.expenseGuid,
                description: this.payment.description,
                caseRequest: {
                  coparentGuid: this.payment.coparentGuid,
                  childrenGuid: this.payment.cases.map((caseIt) => caseIt.childGuid),
                },
                paymentDate: this.requestDateFormat(this.payment.date),
                totalAmount: this.payment.totalAmount,
                paidByGuid: this.payment.paidBy.guid,
                reimbursementAmount: this.payment.reimbursementAmount,
                alreadyPaid: this.payment.alreadyPaid,
                note: this.payment.note === '' ? null : this.payment.note,
                attachments: this.payment.attachments,
                attachmentGuidsForDeletion: this.payment.deletedAttachments
              })
                .then(response => {
                  this.serverErrors = false;
                  this.$snotify.success(this.$lang.messages.sn_paymentSuccesSaved, 'SUCCESS');
                  this.$emit('loadingPayNow', false);
                  this.$emit('loadingAdd', false);
                  this.$emit('loadingAlreadyPaid', false);

                  if (isQuery) {
                    this.$router.push({
                      name: "ExpenseDetailsSharedExpenses",
                      params: {id: response.data.data.guid},
                      query: {pay: true}
                    })
                  } else {
                    this.$router.push({
                      name: "ExpenseDetailsSharedExpenses",
                      params: {id: response.data.data.guid}
                    })
                  }
                })
                .catch(error => {
                  this.$emit('loadingPayNow', false);
                  this.$emit('loadingAdd', false);
                  this.$emit('loadingAlreadyPaid', false);
                  if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                    this.$snotify.error(error.response.data.status.desc, 'ERROR')
                  }
                });
            }
          }
        }
      },
      addPayment(isQuery = false) {
        //*******************************************//
        //------- FPScheduleRecurringPayments -------//
        //*******************************************//
        if (this.QUERY_PATH) {
          if (!this.loading) {
            this.loading = true;
            this.$emit('isLoading', true);
            if (this.type === this.$ALIMONY_EXPENSE_TYPE) {
              this.addNewRecSpouseAction({
                expenseRequestDto: {
                  payorGuid: this.payment.payor.guid,
                  recipientGuid: this.payment.recipient.guid,
                  amount: this.payment.amount,
                  note: this.payment.note === '' ? null : this.payment.note,
                  zoneId: this.zoneIdDateFormat(this.payment.date),
                },
                firstPaymentDate: this.requestDateFormat(this.payment.date),
                intervalDuration: this.findInterval(this.payment.frequencyOption, this.payment.intervalUnit, this.payment.intervalDuration).intervalDuration,
                intervalUnit: this.findInterval(this.payment.frequencyOption, this.payment.intervalUnit, this.payment.intervalDuration).intervalUnit,
                endDate: this.payment.endUnit.value === 'never' || this.payment.endUnit.value === 'after' ? null : this.requestDateFormat(this.payment.endDate),
                numberOfOccurrences: this.payment.endUnit.value === 'after' ? this.payment.endDuration : null,
                basicAttachmentRequestDtos: this.payment.attachments,
                autopayEnabled: this.payment.autopay
              })
                .then(response => {
                  this.serverErrors = false;
                  this.$snotify.success(this.$lang.messages.sn_paymentSuccesSaved, 'SUCCESS');
                  this.loading = false;
                  this.$emit('isLoading', false);
                  this.$router.push({
                    name: "SpousalMaintenanceRecurringExpensesDetails",
                    params: {id: response.data.data.expenseRequestDto.expenseGuid}
                  })
                })
                .catch(error => {
                  this.loading = false;
                  this.$emit('isLoading', false);
                  if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                    this.$snotify.error(error.response.data.status.desc, 'ERROR')
                  }
                });
            } else if (this.type === this.$CHILD_EXPENSE_TYPE) {
              this.addNewRecChildAction({
                expenseRequestDto: {
                  payorGuid: this.payment.payor.guid,
                  recipientGuid: this.payment.recipient.guid,
                  amount: this.payment.amount,
                  note: this.payment.note === '' ? null : this.payment.note,
                  zoneId: this.zoneIdDateFormat(this.payment.date),
                },
                firstPaymentDate: this.requestDateFormat(this.payment.date),
                intervalDuration: this.findInterval(this.payment.frequencyOption, this.payment.intervalUnit, this.payment.intervalDuration).intervalDuration,
                intervalUnit: this.findInterval(this.payment.frequencyOption, this.payment.intervalUnit, this.payment.intervalDuration).intervalUnit,
                endDate: this.payment.endUnit.value === 'never' || this.payment.endUnit.value === 'after' ? null : this.requestDateFormat(this.payment.endDate),
                numberOfOccurrences: this.payment.endUnit.value === 'after' ? this.payment.endDuration : null,
                childrenGuid: this.payment.children,
                basicAttachmentRequestDtos: this.payment.attachments,
                autopayEnabled: this.payment.autopay
              })
                .then(response => {
                  this.serverErrors = false;
                  this.$snotify.success(this.$lang.messages.sn_paymentSuccesSaved, 'SUCCESS');
                  this.loading = false;
                  this.$emit('isLoading', false);
                  this.$router.push({
                    name: "ChildMaintenanceRecurringExpensesDetails",
                    params: {id: response.data.data.expenseRequestDto.expenseGuid}
                  })
                })
                .catch(error => {
                  this.loading = false;
                  this.$emit('isLoading', false);
                  if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                    this.$snotify.error(error.response.data.status.desc, 'ERROR')
                  }
                });
            } else if (this.type === this.$SHARED_EXPENSE_TYPE) {
              this.addNewRecSharedAction({
                description: this.payment.description,
                expenseRequestDto: {
                  payorGuid: this.payment.paidBy.guid,
                  recipientGuid: this.payment.coparentGuid,
                  amount: this.payment.totalAmount,
                  note: this.payment.note === '' ? null : this.payment.note,
                  zoneId: this.zoneIdDateFormat(this.payment.date),
                },
                firstPaymentDate: this.requestDateFormat(this.payment.date),
                intervalDuration: this.findInterval(this.payment.frequencyOption, this.payment.intervalUnit, this.payment.intervalDuration).intervalDuration,
                intervalUnit: this.findInterval(this.payment.frequencyOption, this.payment.intervalUnit, this.payment.intervalDuration).intervalUnit,
                endDate: this.payment.endUnit.value === 'never' || this.payment.endUnit.value === 'after' ? null : this.requestDateFormat(this.payment.endDate),
                numberOfOccurrences: this.payment.endUnit.value === 'after' ? this.payment.endDuration : null,
                childrenGuids: this.payment.cases,
                reimbursementAmount: this.payment.reimbursementAmount,
                basicAttachmentRequestDtos: this.payment.attachments,
                autopayEnabled: this.payment.autopay
              })
                .then(response => {
                  this.serverErrors = false;
                  this.$snotify.success(this.$lang.messages.sn_paymentSuccesSaved, 'SUCCESS');
                  this.loading = false;
                  this.$emit('isLoading', false);
                  this.$router.push({
                    name: "SharedExpensesRecurringExpensesDetails",
                    params: {id: response.data.data.expenseRequestDto.expenseGuid}
                  })
                })
                .catch(error => {
                  this.loading = false;
                  this.$emit('isLoading', false);
                  if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                    this.$snotify.error(error.response.data.status.desc, 'ERROR')
                  }
                });
            }
          }
        }
        //*******************************************//
        //-------------- FPAddPayments --------------//
        //*******************************************//
        else {
          if ((this.payNow && !this.isLoadingPayNow) || (!this.payNow && !this.isLoadingAdd && !this.isLoadingAlreadyPaid)) {
            if (this.payNow) {
              this.$emit('loadingPayNow', true);
            } else if (!this.isAlreadyPaid) {
              this.$emit('loadingAdd', true);
            } else {
              this.$emit('loadingAlreadyPaid', true);
            }
            // Update the local alreadyPaid when user clicks on button "Already Paid",
            // which is located in parent component
            this.payment.alreadyPaid = this.isAlreadyPaid;

            if (this.type === this.$ALIMONY_EXPENSE_TYPE) {
              this.addNewSpouseAction({
                expenseRequestDto: {
                  payorGuid: this.payment.payor.guid,
                  recipientGuid: this.payment.recipient.guid,
                  amount: this.payment.amount,
                  note: this.payment.note === '' ? null : this.payment.note
                },
                paymentDate: this.requestDateFormat(this.payment.date),
                alreadyPaid: this.payment.alreadyPaid,
                attachments: this.payment.attachments
              })
                .then(response => {
                  this.serverErrors = false;
                  this.$snotify.success(this.$lang.messages.sn_paymentSuccesSaved, 'SUCCESS');
                  this.$emit('loadingPayNow', false);
                  this.$emit('loadingAdd', false);
                  this.$emit('loadingAlreadyPaid', false);

                  if (isQuery) {
                    this.$router.push({
                      name: "ExpenseDetailsSpousalMaintenance",
                      params: {id: response.data.data.guid},
                      query: {pay: true}
                    })
                  } else {
                    this.$router.push({
                      name: "ExpenseDetailsSpousalMaintenance",
                      params: {id: response.data.data.guid}
                    })
                  }
                })
                .catch(error => {
                  this.$emit('loadingPayNow', false);
                  this.$emit('loadingAdd', false);
                  this.$emit('loadingAlreadyPaid', false);
                  if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                    this.$snotify.error(error.response.data.status.desc, 'ERROR')
                  }
                });
            } else if (this.type === this.$CHILD_EXPENSE_TYPE) {
              this.addNewChildAction({
                expenseRequestDto: {
                  payorGuid: this.payment.payor.guid,
                  recipientGuid: this.payment.recipient.guid,
                  amount: this.payment.amount,
                  note: this.payment.note === '' ? null : this.payment.note
                },
                children: this.payment.chosenChildren.map((child) => child.childGuid),
                paymentDate: this.requestDateFormat(this.payment.date),
                alreadyPaid: this.payment.alreadyPaid,
                attachments: this.payment.attachments
              })
                .then(response => {
                  this.serverErrors = false;
                  this.$snotify.success(this.$lang.messages.sn_paymentSuccesSaved, 'SUCCESS');
                  this.$emit('loadingPayNow', false);
                  this.$emit('loadingAdd', false);
                  this.$emit('loadingAlreadyPaid', false);

                  if (isQuery) {
                    this.$router.push({
                      name: "ExpenseDetailsChildMaintenance",
                      params: {id: response.data.data.guid},
                      query: {pay: true}
                    })
                  } else {
                    this.$router.push({
                      name: "ExpenseDetailsChildMaintenance",
                      params: {id: response.data.data.guid}
                    })
                  }
                })
                .catch(error => {
                  this.$emit('loadingPayNow', false);
                  this.$emit('loadingAdd', false);
                  this.$emit('loadingAlreadyPaid', false);
                  if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                    this.$snotify.error(error.response.data.status.desc, 'ERROR')
                  }
                });
            } else if (this.type === this.$SHARED_EXPENSE_TYPE) {
              this.addNewSharedAction({
                description: this.payment.description,
                caseRequest: {
                  coparentGuid: this.payment.coparentGuid,
                  childrenGuid: this.payment.cases.map((caseIt) => caseIt.childGuid),
                },
                paymentDate: this.requestDateFormat(this.payment.date),
                totalAmount: this.payment.totalAmount,
                paidByGuid: this.payment.paidBy.guid,
                reimbursementAmount: this.payment.reimbursementAmount,
                alreadyPaid: this.payment.alreadyPaid,
                note: this.payment.note === '' ? null : this.payment.note,
                attachments: this.payment.attachments
              })
                .then(response => {
                  this.serverErrors = false;
                  this.$snotify.success(this.$lang.messages.sn_paymentSuccesSaved, 'SUCCESS');
                  this.$emit('loadingPayNow', false);
                  this.$emit('loadingAdd', false);
                  this.$emit('loadingAlreadyPaid', false);

                  if (isQuery) {
                    this.$router.push({
                      name: "ExpenseDetailsSharedExpenses",
                      params: {id: response.data.data.guid},
                      query: {pay: true}
                    })
                  } else {
                    this.$router.push({
                      name: "ExpenseDetailsSharedExpenses",
                      params: {id: response.data.data.guid}
                    })
                  }
                })
                .catch(error => {
                  this.$emit('loadingPayNow', false);
                  this.$emit('loadingAdd', false);
                  this.$emit('loadingAlreadyPaid', false);
                  if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                    this.$snotify.error(error.response.data.status.desc, 'ERROR')
                  }
                });
            }
          }
        }
      },
      // TODO: Candidate for global (Payments) function or mixin
      deleteAttachment(guid) {
        this.payment.deletedAttachments.push(guid);
        this.payment.expenseAttachments = this.payment.expenseAttachments.filter(value => {
          return value.attachmentGuid !== guid
        })
      },
      // TODO: Candidate for global (Payments) function or mixin
      deleteUploadAttachment(fileName) {
        this.payment.attachments = this.payment.attachments.filter(value => {
          return value.fileName !== fileName
        })
      },
      // TODO: Candidate for global (Payments) function or mixin
      onChangeFile(file) {
        if (file !== '') {
          this.payment.attachments.push({
            fileName: file.name,
            mimeType: file.type,
            encodedFile: file.base64
          })
        }
      },
      /**
       * @description From child component (FPTextareaNote) listen to the textarea input/value, update the prop
       * and send the value back to the children
       * @param {String} val
       * @return {String}
       */
      textareaInput (val) {
        this.payment.note = val
      },
      autofillCoparents() {
        if (this.actionType === 'add' && (this.coparents.length === 2 || (this.cases.length === 1 && this.cases[0].children.length === 1))) {
          if (this.type === this.$SHARED_EXPENSE_TYPE) {
            // Originally paid By
            this.payment.paidBy = this.findCoparentMe();
            // Calculate Reimbursement payor
            this.arrayCases.push(this.findCoparentMe());
            this.arrayCases.push(this.findCoparentOther(this.findCoparentMe()));
            // Call the function to print the Reimbursement payor
            this.reimbursementPayorName();
            // Rerender the function
            this.rerender+=1;
          } else {
            this.payment.payor = this.findCoparentMe();
            this.payment.recipient = this.coparents.find(element => {
              return element.guid !== this.coparentGuid;
            });
            this.loadCoparents(this.payment.recipient.guid);
            this.rerender+=1;
          }
        }
        this.isPayDisabled();
      },
      autofillChildren() {
        if (this.actionType === 'add' && this.children.length === 1) {
          this.children[0].clicked = true;
          this.payment.chosenChildren = this.children;
          //*******************************************//
          //------- FPScheduleRecurringPayments -------//
          //*******************************************//
          if (this.QUERY_PATH) {
            this.payment.children = this.payment.chosenChildren.map((child) => child.childGuid);
          }
          this.rerenderChecklist+=1;
        }
      },
      autofillCases() {
        if (this.type === this.$SHARED_EXPENSE_TYPE && this.actionType === 'add') {
          if (this.cases.length === 1) {
            if (this.cases[0].children.length === 1) {
              this.cases[0].children[0].clicked = true;
              //*******************************************//
              //------- FPScheduleRecurringPayments -------//
              //*******************************************//
              if (this.QUERY_PATH) {
                this.payment.cases.push(this.cases[0].children[0].childGuid);
              }
              //*******************************************//
              //-------------- FPAddPayments --------------//
              //*******************************************//
              else {
                this.payment.cases.push(this.cases[0].children[0]);
              }
              this.payment.coparentGuid = this.cases[0].coparentGuid;
              this.payment.coparentName = this.cases[0].coparentName;
              this.payment.coparentShortenedName = this.cases[0].coparentShortenedName;
              this.payment.casesExists = true;
              this.rerenderChecklist+=1;
            }
          }
        }
      },
      reimbursementPayorName() {
        if (this.payment.paidBy.guid === this.coparentGuid) {
          this.printReimbursementPayor.name = this.findCoparentOther(this.payment.paidBy, this.arrayCases).name;
          this.printReimbursementPayor.shortenedName = this.findCoparentOther(this.payment.paidBy, this.arrayCases).shortenedName;
          this.printReimbursementPayor.guid = this.findCoparentOther(this.payment.paidBy, this.arrayCases).guid;
          this.printReimbursementPayor.canReceiveFunds = this.findCoparentOther(this.payment.paidBy, this.arrayCases).canReceiveFunds
        } else {
          this.printReimbursementPayor.name = this.coparentName;
          this.printReimbursementPayor.shortenedName = this.coparentShortenedName;
          this.printReimbursementPayor.guid = this.coparentGuid;
        }
        this.calculateReimbursementAmount();
        //*******************************************//
        //-------------- FPAddPayments --------------//
        //*******************************************//
        if (!this.QUERY_PATH) {
          this.isPayDisabled();
        }
      },
      //*******************************************//
      //-------------- FPAddPayments --------------//
      //*******************************************//
      /**
       *
       * @description Via $root.on listen in mounted for 'already-paid' key,
       * call the function for form submission
       *
       */
      alreadyPaidChange() {
        this.$emit('alreadyPaid', true);
        this.onAddNewFormSubmitted();
      },
      /**
       * @description On one-time payment enable or disable the "Pay Now" button
       * @returns {boolean}
       */
      isPayDisabled() {
        let disabled = true;
        if (!this.QUERY_PATH) {

          if (this.type !== this.$SHARED_EXPENSE_TYPE) {
            if (this.payment.payor.guid === this.coparentGuid) {
              disabled = !(this.payment.payor.canReceiveFunds && this.payment.recipient.canReceiveFunds);
            }
          }

          if (this.type === this.$SHARED_EXPENSE_TYPE) {
            if (this.printReimbursementPayor.guid === this.coparentGuid) {
              disabled = false;
            }
          }
        }
        this.$root.$emit('pay-disabled', {"prop" : disabled, "text" : this.notPayableReason});
      },
      //*******************************************//
      //------- FPScheduleRecurringPayments -------//
      //*******************************************//
      /**
       * @description When we are on shared recurring expense, and user fills out the form
       * then submit the form but didn't entered the Total Amount and Reimbursement amount we want
       * to disable validation message on Reimbursement amount when user clicks on Total Amount
       */
      checkValidationForSharedAmount () {
        this.$validator.errors.remove('reimbursementAmount')
      },
      /**
       *
       * @description Via Vuex, call the endpoint for fetching interval units
       *
       */
      getIntervalData() {
        this.getInterval()
          .then(res => {
            res.data.data.forEach(elem => {
              this.intervalUnits.push(elem)
            });
            this.payment.intervalUnit = this.intervalUnits[0]
          })
          .catch(() => this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR'))
      },
      /**
       * @description Enable or disable checkbox for Autopay,
       * emits the Boolean value to the parent component ( FPScheduleRecurringPayment )
       * @returns {boolean}
       */
      isAutopayDisabled() {
        let disabled = false;

        if (this.type !== this.$SHARED_EXPENSE_TYPE) {
          if (this.payment.payor.guid !== '') {
            if (this.payment.payor.guid !== this.coparentGuid) {
              disabled = true;
            } else if (this.payment.recipient.guid !== '') {
              disabled = !(this.canSendFunds && this.payment.payor.canReceiveFunds && this.payment.recipient.canReceiveFunds);
            }
          }
        }

        if (this.type === this.$SHARED_EXPENSE_TYPE) {
          if (this.printReimbursementPayor.guid !== this.coparentGuid) {
            disabled = true;
          } else {
            disabled = !this.canSendFunds;
          }
        }

        if (disabled) {
          this.payment.autopay = false;
        }

        return disabled;
      },
      /**
       * @description
       * @param {string} unit
       * @param {number} duration
       * @returns
       */
      findIntervalUnit(unit, duration) {
        unit = unit.toLowerCase();
        switch (unit) {
          case 'week':
            if (duration === 2) {
              return this.findInterval(this.frequencyOptions[1], this.findInIntervalUnit(unit), duration);
            }
            else if (duration === 1) {
              return this.findInterval(this.frequencyOptions[0], this.findInIntervalUnit(unit), duration);
            }
            break;
          case 'month':
            if (duration === 2) {
              return this.findInterval(this.frequencyOptions[3], this.findInIntervalUnit(unit), duration);
            }
            else if (duration === 1) {
              return this.findInterval(this.frequencyOptions[2], this.findInIntervalUnit(unit), duration);
            }
            break;
          default:
            return this.findInterval(this.frequencyOptions[3], this.findInIntervalUnit(unit), duration);
        }
      },
      /**
       * @description
       * @param {string} name
       * @returns
       */
      findInIntervalUnit(name) {
        return this.intervalUnits.find(function (element) {
          return element.name === name.toLowerCase();
        })
      },
      findInterval(interval, customUnit, customDuration) {
        let intervalUnit = '';
        let intervalUnitName = '';
        let intervalDuration = 0;
        let month = '';
        let week = '';
        let intervalUnitObject = {
          value: '',
          name: ''
        };

        for (let x of this.intervalUnits) {
          if (x.name === 'month') {
            month = x.value;
          }
          if (x.name === 'week') {
            week = x.value;
          }
        }

        switch (interval.value) {
          case 'weekly':
            intervalUnit = week;
            intervalDuration = 1;
            intervalUnitName = 'week';
            intervalUnitObject = {
              value: intervalUnit,
              name: intervalUnitName
            };
            return {
              intervalUnit: intervalUnit,
              intervalUnitName: intervalUnitName,
              intervalDuration: intervalDuration,
              intervalUnitObject: intervalUnitObject,
              interval: interval
            };
          case 'twoWeekly':
            intervalUnit = week;
            intervalDuration = 2;
            intervalUnitName = 'week';
            intervalUnitObject = {
              value: intervalUnit,
              name: intervalUnitName
            };
            return {
              intervalUnit: intervalUnit,
              intervalUnitName: intervalUnitName,
              intervalDuration: intervalDuration,
              intervalUnitObject: intervalUnitObject,
              interval: interval
            };
          case 'monthly':
            intervalUnit = month;
            intervalDuration = 1;
            intervalUnitName = 'month';
            intervalUnitObject = {
              value: intervalUnit,
              name: intervalUnitName
            };
            return {
              intervalUnit: intervalUnit,
              intervalUnitName: intervalUnitName,
              intervalDuration: intervalDuration,
              intervalUnitObject: intervalUnitObject,
              interval: interval
            };
          case 'custom':
            intervalUnit = customUnit.value;
            intervalDuration = customDuration;
            intervalUnitName = customUnit.name;
            intervalUnitObject = {
              value: intervalUnit,
              name: intervalUnitName
            };
            return {
              intervalUnit: intervalUnit,
              intervalUnitName: intervalUnitName,
              intervalDuration: intervalDuration,
              intervalUnitObject: intervalUnitObject,
              interval: interval
            };
          default:
            intervalUnit = '';
            intervalDuration = 0;
            intervalUnitName = '';
            intervalUnitObject = {
              value: intervalUnit,
              name: intervalUnitName
            };
            return {
              intervalUnit: intervalUnit,
              intervalUnitName: intervalUnitName,
              intervalDuration: intervalDuration,
              intervalUnitObject: intervalUnitObject,
              interval: interval
            }
        }
      },
      // TODO: Consider this function to be a global function or mixin
      dateUTC(date) {
        date = new Date(date);
        // date.setTime(date.getTime() + Math.abs(date.getTimezoneOffset()) * 60000);
        return date;
      },
      /**
       *
       * @description
       *
       */
      clickAutopay() {
        this.payment.autopay = !this.payment.autopay;
      },
      onSelectedFrequencyOption(value) {
        this.payment.frequencyOption = value;
        this.drawCalendar();
      },
      onSelectedIntervalUnit(value) {
        if (value.name === 'day' || value.name === 'week' || value.name === 'month' || value.name === 'year') {
          this.payment.intervalUnit = value
        }
        this.drawCalendar()
      },
      onSelectedEndUnit(value) {
        if (value.value === 'on' || value.value === 'after' || value.value === 'never') {
          this.drawCalendar()
        }
        this.payment.endUnit = value
      },
      onSelectedEndDate(value) {
        this.payment.endDate = new Date(value);
        this.drawCalendar();
      },
      drawCalendar() {
        this.calculateEndDate();
        this.$refs.calendar.drawCalendar(
          this.payment.date,
          this.payment.endDate,
          this.payment.endUnit.value,
          this.findInterval(this.payment.frequencyOption, this.payment.intervalUnit, this.payment.intervalDuration).intervalUnitName,
          this.findInterval(this.payment.frequencyOption, this.payment.intervalUnit, this.payment.intervalDuration).intervalDuration,
          this.payment.endDuration
        )
      },
      setCalendarAttrs(value) {
        this.calendarAttrs = value
      },
      calculateEndDate() {
        if (this.payment.frequencyOption.value === 'weekly') {
          if (this.payment.endUnit.value === 'after') {
            this.payment.endDate.setDate(this.dateUTC(this.payment.date).getDate() + (this.payment.endDuration - 1) * 7)
          }
        } else if (this.payment.frequencyOption.value === 'twoWeekly') {
          if (this.payment.endUnit.value === 'after') {
            this.payment.endDate.setDate(this.dateUTC(this.payment.date).getDate() + (this.payment.endDuration - 1) * 2 * 7)
          }
        } else if (this.payment.frequencyOption.value === 'monthly') {
          if (this.payment.endUnit.value === 'after') {
            this.payment.endDate = this.addMonths(this.dateUTC(this.payment.date), (this.payment.endDuration -1) * this.payment.intervalDuration)
          }
        } else if(this.payment.frequencyOption.value === 'custom') {
          if (this.payment.endUnit.value === 'after') {
            if (this.payment.intervalUnit.value === 'INTERVAL_UNIT_DAY') {
              this.payment.endDate.setDate(this.dateUTC(this.payment.date).getDate() + (this.payment.endDuration - 1) * this.payment.intervalDuration)
            } else if (this.payment.intervalUnit.value === 'INTERVAL_UNIT_WEEK') {
              this.payment.endDate.setDate(this.dateUTC(this.payment.date).getDate() + (this.payment.endDuration - 1) * this.payment.intervalDuration * 7)
            } else if (this.payment.intervalUnit.value === 'INTERVAL_UNIT_MONTH') {
              this.payment.endDate = this.addMonths(this.dateUTC(this.payment.date), (this.payment.endDuration -1) * this.payment.intervalDuration)
            } else if (this.payment.intervalUnit.value === 'INTERVAL_UNIT_MONTH') {
              this.payment.endDate = this.addYears(this.dateUTC(this.payment.date), (this.payment.endDuration -1) * this.payment.intervalDuration)
            }
          }
        }
      },
      showTooltip(tooltipNo) {
        this.tooltipId = tooltipNo
      },
      onClickOutside() {
        this.tooltipId = 0
      },
      hideTooltip() {
        this.tooltipId = 0
      },
      generateShortenedName(coparent) {
        let splitName = coparent.name.trim().match(/\S+/g);
        return splitName[0] + ' ' + splitName[1].substring(0, 1) + '.';
      },
      preselectPayor() {
        for (let i of this.coparentsMaster) {
          if (i.guid === this.coparentGuid) {
            if (this.type !== this.$SHARED_EXPENSE_TYPE) {
              this.payment.payor = i;
            } else {
              this.payment.paidBy = i;
            }
          }
        }
      }
    }
  }
</script>
